/**
 * De labopdracht als document -- de gegevens, niet de opmaak.
 *
 * Een labopdracht wordt op drie plekken uitgeschreven: als blad op het scherm, als bevestiging
 * vóór het versturen, en als PDF die met de monsters meegaat naar het lab. Die uit elkaar laten
 * lopen is een kwestie van tijd, dus staat hier één keer wát er in het document hoort en in
 * welke volgorde, en bepalen de tekenaars alleen nog hóe het eruitziet.
 *
 * De indeling volgt het labopdrachtformulier zoals de branche dat kent (TerraIndex drukt hem zo
 * af): kerngegevens van het project, de afspraken met het lab, een overzicht van welke analyse
 * op welk analysemonster komt, en daarna per analysemonster de deelmonsters (potten) waaruit
 * het lab het mengmonster samenstelt. Locatie- en boorgegevens blijven erbuiten -- die gaan ook
 * niet naar het lab.
 *
 * Bewust zonder Lit, zonder CSS en zonder @mv/models: zo is het te testen met `node --test`.
 * Het label bij een verpakkingscode komt daarom als functie mee.
 */

const LEEG = '—';

/** ISO-datum (met of zonder tijd) → 28-09-2026. Leeg blijft leeg. */
export function documentDatum(waarde) {
  if (!waarde) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(waarde).trim());
  if (!m) return String(waarde);
  return `${m[3]}-${m[2]}-${m[1]}`;
}

/** Datum + tijd, voor de regel 'verzonden op'. Bewaarde tijden zijn UTC zonder zone-aanduiding. */
export function documentDatumTijd(waarde) {
  if (!waarde) return '';
  let s = String(waarde).trim().replace(' ', 'T');
  if (!/[zZ]$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  if (isNaN(d)) return String(waarde);
  // Geen vaste tijdzone: het document toont dezelfde klok als het scherm waar je het van maakt.
  const p = Object.fromEntries(new Intl.DateTimeFormat('nl-NL', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(d).map(x => [x.type, x.value]));
  return `${p.day}-${p.month}-${p.year} ${p.hour}:${p.minute}`;
}

/** Een getal als tekst, of leeg. 0 is een waarde en blijft dus staan. */
function getal(waarde) {
  return waarde === null || waarde === undefined || waarde === '' ? '' : String(waarde);
}

const MATRIX_LABEL = {
  grond: 'Grond', water: 'Grondwater', grondwater: 'Grondwater', waterbodem: 'Waterbodem', asfalt: 'Asfalt', lucht: 'Lucht',
};

/**
 * @param {object} opties
 * @param {object} opties.order          de labopdracht met analysisSamples
 * @param {object} [opties.project]      het project (naam en code)
 * @param {object} [opties.connection]   de labkoppeling (naam, labId, urgenties, afspraken)
 * @param {Array}  [opties.borings]      boringen van het project, met monsters: voor meetpunt,
 *                                       monsternemer en geroerd/ongeroerd
 * @param {string} [opties.companyName]  de opdrachtgever
 * @param {string} [opties.companyLogo]  het beeldmerk van de opdrachtgever (data-URI)
 * @param {(code:string)=>string} [opties.verpakkingLabel]
 * @param {(x:{sampleName:string, meetpunten:string[]})=>string} [opties.certificaatcode]
 *        de code waaronder het lab dit analysemonster rapporteert; standaard de naam zelf.
 *        De regel zelf staat in @mv/models, die hier niet te laden is zonder bundelaar.
 */
export function buildLabopdrachtDocument({
  order = {}, project = null, connection = null, borings = [], companyName = '', companyLogo = '',
  verpakkingLabel = (c) => c || '',
  certificaatcode = ({ sampleName }) => sampleName,
} = {}) {
  const boringOp = new Map((borings || []).map(b => [b.id, b]));
  // Monsters liggen bij hun boring; het deelmonster verwijst er met monsterId naar. Zo komen
  // geroerd/ongeroerd en de monsternemer in het document zonder dat ze in de labopdracht zelf
  // bewaard hoeven te worden.
  const monsterOp = new Map();
  for (const b of borings || []) {
    for (const m of b.monsters || []) monsterOp.set(m.id || m.localId, { monster: m, boring: b });
  }

  const uitLijst = (lijst, code) => {
    if (!code) return '';
    const hit = (connection?.[lijst] || []).find(x => String(x.code) === String(code));
    return hit ? hit.label : String(code);
  };

  const projectNaam = project?.project_name || project?.name || project?.project_code || '';
  const projectCode = project?.project_code || project?.projectCode || '';
  const labNaam = connection?.displayName || (order.labKey === 'sgs' ? 'SGS' : order.labKey || '');

  const samples = (order.analysisSamples || [])
    .filter(s => (s.sampleKind || 'mengmonster') === 'mengmonster');

  const mengmonsters = samples.map(sample => {
    const parts = sample.parts || [];
    const deelmonsters = parts.map(p => {
      const bron = monsterOp.get(p.monsterId);
      const boring = boringOp.get(p.boringId) || bron?.boring;
      // Geroerd staat in de labopdracht zelf als het potje na deze wijziging is toegevoegd;
      // anders komt het van het monster bij de boring. Ongeroerd is de uitzondering (steekbus),
      // dus alleen een uitgesproken `false` betekent ongeroerd.
      const geroerd = p.observations?.geroerd ?? bron?.monster?.geroerd;
      return {
        naam: p.observations?.naam || bron?.monster?.name || p.barcode || '',
        barcode: p.barcode || '',
        meetpunt: p.observations?.boring || boring?.name || '',
        van: getal(p.upperDepth),
        tot: getal(p.lowerDepth),
        datum: documentDatum(p.observations?.datum || bron?.monster?.datum),
        // Een fles grondwater: gefiltreerd of niet (metalen gaan gefiltreerd naar het lab).
        verpakking: [p.observations?.verpakking ? verpakkingLabel(p.observations.verpakking) : '',
          p.observations?.gefiltreerd === undefined ? '' : (p.observations.gefiltreerd ? 'gefiltreerd' : 'ongefiltreerd')]
          .filter(Boolean).join(', '),
        geroerd: p.observations?.gefiltreerd !== undefined || geroerd === undefined || geroerd === null ? ''
          : (geroerd === false ? 'Nee' : 'Ja'),
      };
    });

    const datums = [...new Set(deelmonsters.map(d => d.datum).filter(Boolean))];

    return {
      naam: sample.name || '',
      matrix: MATRIX_LABEL[sample.matrix] || MATRIX_LABEL[order.sampleType] || 'Grond',
      // De code waaronder het lab rapporteert, uit het sjabloon van deze opdracht. Niet te
      // verwarren met <certificaat> in het aanleverbestand: daar staat dezelfde code, maar
      // met de monsternamen erachter -- en die lijst hoort in het bestand, niet op een blad
      // dat je leest.
      certificaatcode: certificaatcode({
        sampleName: sample.name || '',
        meetpunten: deelmonsters.map(d => d.meetpunt).filter(Boolean),
      }) || sample.name || '',
      monsternamedatum: datums.length === 1 ? datums[0] : (datums.length ? `${datums[0]} e.v.` : ''),
      notitie: sample.reason || '',
      van: parts.length ? Math.min(...parts.map(p => p.upperDepth ?? 0)) : sample.upperDepth,
      tot: parts.length ? Math.max(...parts.map(p => p.lowerDepth ?? 0)) : sample.lowerDepth,
      analyses: (sample.packages || []).map(p => ({ code: p.code || '', naam: p.name || '' })),
      deelmonsters,
    };
  });

  // De analysecodes over alle mengmonsters heen, in de volgorde waarin je ze tegenkomt. Dat is
  // de kolomindeling van het overzicht: één kolom per code, een vinkje waar hij aanstaat.
  const codes = [];
  for (const m of mengmonsters) {
    for (const a of m.analyses) {
      if (!codes.some(c => c.code === a.code && c.naam === a.naam)) codes.push({ ...a });
    }
  }
  const heeft = (m, c) => m.analyses.some(a => a.code === c.code && a.naam === c.naam);

  /*
   * De matrix draait om zodra er meer analyses dan mengmonsters zijn.
   *
   * Normaal staan de analysemonsters onder elkaar en de analyses ernaast: een opdracht heeft
   * meestal meer monsters dan analyses. Andersom loopt de tabel de bladzijde uit -- acht
   * analyses op twee monsters paste al niet meer in het bevestigingsvenster. De kolommen zijn
   * dus altijd de kortste van de twee; het kopje van de eerste kolom zegt welke kant het op
   * staat.
   */
  const gedraaid = codes.length > mengmonsters.length && mengmonsters.length > 0;

  const matrix = gedraaid ? {
    gedraaid: true,
    rijkop: 'Analyse',
    vaste: [],
    kolommen: mengmonsters.map(m => ({
      titel: m.naam,
      onder: `${m.matrix} · ${m.deelmonsters.length} ${m.deelmonsters.length === 1 ? 'pot' : 'potten'}`,
    })),
    rijen: codes.map(c => ({
      titel: c.code, onder: c.naam, vast: [],
      vinkjes: mengmonsters.map(m => heeft(m, c)),
    })),
    voettitel: 'Aantal analyses',
    totalen: mengmonsters.map(m => codes.filter(c => heeft(m, c)).length),
  } : {
    gedraaid: false,
    rijkop: 'Analysemonster',
    vaste: ['Matrix', 'Potten'],
    kolommen: codes.map(c => ({ titel: c.code, onder: c.naam })),
    rijen: mengmonsters.map(m => ({
      titel: m.naam, onder: '',
      vast: [m.matrix, String(m.deelmonsters.length)],
      vinkjes: codes.map(c => heeft(m, c)),
    })),
    voettitel: 'Aantal analysemonsters',
    totalen: codes.map(c => mengmonsters.filter(m => heeft(m, c)).length),
  };

  const aantalPotten = mengmonsters.reduce((n, m) => n + m.deelmonsters.length, 0);

  // Wie de monsters gestoken heeft, hoort bij de boring en niet bij het analysemonster: een
  // mengmonster is samengesteld uit potten van meerdere boringen en heeft dus geen eigen
  // monsternemer. Op orderniveau klopt het wel -- dit zijn de mensen die het veldwerk deden.
  const nemers = [...new Set((order.analysisSamples || [])
    .flatMap(s => s.parts || [])
    .map(p => (boringOp.get(p.boringId) || monsterOp.get(p.monsterId)?.boring)?.boormeester)
    .filter(Boolean))];

  return {
    titel: 'Labopdracht',
    nummer: order.number || '',
    labNaam,
    labId: connection?.labId || '',
    labOrderId: order.labOrderId || '',
    projectNaam,
    projectCode,
    opdrachtgever: companyName || '',
    // Het formulier is van het bedrijf dat de opdracht geeft; zijn beeldmerk staat daarom
    // linksboven. Zonder eigen logo valt het terug op dat van GeoVoice (zie de tekenaars).
    logo: companyLogo || '',
    verzonden: !!(order.sentAt || order.status === 'sent'),
    verzondenOp: order.sentAt || null,
    statustekst: (order.sentAt || order.status === 'sent')
      ? `Verzonden naar ${labNaam || 'het lab'}${order.sentAt ? ` · ${documentDatumTijd(order.sentAt)}` : ''}`
      : 'Concept — nog niet verstuurd',

    kerngegevens: [
      { label: 'Projectnaam', waarde: projectNaam },
      { label: 'Projectcode', waarde: projectCode, mono: true },
      // Kort houden: 'Klantcode SGS Environmental Analytics B.V.' loopt over het vak ernaast.
      { label: `Klantcode ${(order.labKey || '').toUpperCase() || 'lab'}`, waarde: order.labCustomerId || '', mono: true },
      { label: 'Volgnummer', waarde: order.number || '', mono: true },
    ],

    gegevens: {
      links: [
        { k: 'Opdrachtgever', v: companyName || '' },
        { k: 'Projectleider', v: order.projectLeaderName || '' },
        { k: 'E-mail projectleider', v: order.projectLeaderEmail || '' },
        { k: nemers.length > 1 ? 'Monsternemers' : 'Monsternemer', v: nemers.join(', ') },
        { k: 'Laboratorium', v: labNaam, extra: connection?.labId ? `(${connection.labId})` : '' },
        { k: 'Prijsafspraak', v: uitLijst('agreements', order.agreementCode) },
      ],
      // Zonder 'gewenste rapportage' en 'opmaak certificaat': het lab werkt op de afgesproken
      // urgentie en drukt de certificaatcode af die per analysemonster op het blad staat, dus
      // die twee velden vertellen de lezer van dit formulier niets.
      rechts: [
        { k: 'Urgentie', v: uitLijst('urgencies', order.urgencyCode) },
        { k: 'Datum labopdracht', v: documentDatum(order.orderDate) },
        { k: 'Monsterlevering', v: documentDatum(order.sampleDeliveryDate) },
        { k: 'Aantal potten', v: aantalPotten
          ? `${aantalPotten} (${mengmonsters.length} ${mengmonsters.length === 1 ? 'analysemonster' : 'analysemonsters'})`
          : '' },
        { k: 'Order referentie', v: order.reference || '' },
      ],
    },
    opmerking: order.remarks || '',

    analysecodes: codes,
    matrix,
    mengmonsters,
    aantalMengmonsters: mengmonsters.length,
    aantalPotten,

    // Het derde blad van de PDF: wie er tekent bij afgifte, vervoer en ontvangst. Op papier,
    // want daar gebeurt het -- de koerier neemt geen scherm mee.
    overdracht: {
      partijen: [
        { rol: 'Opdrachtgever', wie: companyName || '', naam: order.projectLeaderName || '', datumveld: 'Datum / tijd afgifte' },
        { rol: 'Koerier', wie: '', naam: '', datumveld: 'Datum / tijd ophalen' },
        { rol: 'Ontvangst laboratorium', wie: labNaam, naam: '', datumveld: 'Datum / tijd ontvangst' },
      ],
    },
  };
}

/** "8–68 cm" of leeg als er geen dieptes zijn. */
export function diepteBereik(m) {
  if (m.van === null || m.van === undefined || m.tot === null || m.tot === undefined) return '';
  return `${m.van}–${m.tot} cm`;
}
